import React, { useState } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { PaymentIntentResponse } from '@/lib/api/generated/models'
import { ExternalLink, Copy, Check } from 'lucide-react'

interface PaymentResponseModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  response: PaymentIntentResponse | null
}

export function PaymentResponseModal({ open, onOpenChange, response }: PaymentResponseModalProps) {
  const [copiedField, setCopiedField] = useState<string | null>(null)

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(fieldName)
      setTimeout(() => setCopiedField(null), 2000)
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
  }

  const openRedirectUrl = () => {
    if (response?.redirectUrl) {
      const sessionId = response.sessionId
      const email = response.customer?.email || ''
      const payload = encodeURIComponent(JSON.stringify(response))

      // Determine base URL - use current origin for SDK render page
      const baseUrl = window.location.origin
      const url = `${baseUrl}/sdk-render?sessionId=${sessionId}${
        email ? `&email=${encodeURIComponent(email)}` : ''
      }&payload=${payload}`

      window.open(url, '_blank')
    }
  }

  const openSDKRender = () => {
    if (response) {
      const sessionId = response.sessionId
      const email = response.customer?.email || ''
      const payload = encodeURIComponent(JSON.stringify(response))

      const url = `/sdk-render?sessionId=${sessionId}${
        email ? `&email=${encodeURIComponent(email)}` : ''
      }&payload=${payload}`
      window.open(url, '_blank')
    }
  }

  // Compute the SDK render URL to display
  const getSDKRenderUrl = () => {
    if (!response) return ''
    const sessionId = response.sessionId
    const email = response.customer?.email || ''
    const payload = encodeURIComponent(JSON.stringify(response))
    const baseUrl = window.location.origin
    return `${baseUrl}/sdk-render?sessionId=${sessionId}${
      email ? `&email=${encodeURIComponent(email)}` : ''
    }&payload=${payload}`
  }

  if (!response) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Check className="h-5 w-5 text-green-600" />
            Payment Intent Created Successfully
          </DialogTitle>
          <DialogDescription>Your payment intent has been created. Here are the details:</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Order ID Section */}
          {response.orderDetails?.orderId && (
            <div className="space-y-3">
              <Label className="text-sm font-medium">Order ID</Label>
              <div className="flex items-center gap-2">
                <Input value={response.orderDetails.orderId} readOnly className="font-mono text-sm" />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(response.orderDetails!.orderId, 'orderId')}
                  className="flex items-center gap-1"
                >
                  {copiedField === 'orderId' ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Session ID Section */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Session ID</Label>
            <div className="flex items-center gap-2">
              <Input value={response.sessionId} readOnly className="font-mono text-sm" />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(response.sessionId, 'sessionId')}
                className="flex items-center gap-1"
              >
                {copiedField === 'sessionId' ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Redirect URL Section */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Redirect URL</Label>
            <div className="flex items-center gap-2">
              <Input value={getSDKRenderUrl()} readOnly className="font-mono text-sm" />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(getSDKRenderUrl(), 'redirectUrl')}
                className="flex items-center gap-1"
              >
                {copiedField === 'redirectUrl' ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={openRedirectUrl}
                className="flex items-center gap-1"
              >
                <ExternalLink className="h-4 w-4" />
                Open
              </Button>
            </div>
          </div>

          {/* Additional Details */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t">
            <div>
              <Label className="text-sm font-medium">Total Amount</Label>
              <p className="text-sm text-muted-foreground">
                ${(response.totalAmount / 100).toFixed(2)} {response.currency}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium">Status</Label>
              <p className="text-sm text-muted-foreground capitalize">{response.sessionStatus.toLowerCase()}</p>
            </div>
            <div>
              <Label className="text-sm font-medium">Expires At</Label>
              <p className="text-sm text-muted-foreground">{new Date(response.expiresAt).toLocaleString()}</p>
            </div>
            <div>
              <Label className="text-sm font-medium">Correlation ID</Label>
              <p className="text-sm text-muted-foreground font-mono">{response.correlationId}</p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button type="button" onClick={openSDKRender} className="flex items-center gap-2">
            <ExternalLink className="h-4 w-4" />
            Render Payment Form
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
